import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "hooks/redux";
import { LocaleCurrency } from "helpers/Locale";

import Input from "components/form/input/Input";
import { Select } from "components/select/";
import { ButtonNew } from "components/buttonNew";

export type TTipAllocationRow = {
  user_id: number;
  amount: number;
};

interface ITipAllocationEditorProps {
  tipTotal: number;
  value?: TTipAllocationRow[];
  onChange: (allocations: TTipAllocationRow[] | null) => void;
  currency?: string;
  darkStyle?: boolean;
}

function roundMoney(amount: number) {
  return Math.round(amount * 100) / 100;
}

export default function TipAllocationEditor(props: ITipAllocationEditorProps) {
  const { t } = useTranslation();
  const { Option } = Select;
  const facilityStore = useAppSelector(store => store.facilityStore);
  const authStore = useAppSelector(store => store.authStore);
  const staffAccounts = facilityStore.staffAccounts ?? [];

  const [rows, setRows] = useState<Array<{ user_id: number | null; amount: string }>>([
    { user_id: authStore.user?.id ?? null, amount: props.tipTotal > 0 ? roundMoney(props.tipTotal).toFixed(2) : "0.00" },
  ]);

  useEffect(() => {
    if (props.value?.length) {
      setRows(
        props.value.map(row => ({
          user_id: row.user_id,
          amount: roundMoney(row.amount).toFixed(2),
        })),
      );
      return;
    }
    setRows([
      {
        user_id: authStore.user?.id ?? null,
        amount: props.tipTotal > 0 ? roundMoney(props.tipTotal).toFixed(2) : "0.00",
      },
    ]);
  }, [props.tipTotal, props.value, authStore.user?.id]);

  const allocatedTotal = useMemo(
    () => roundMoney(rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)),
    [rows],
  );
  const remaining = roundMoney(props.tipTotal - allocatedTotal);
  const isValid =
    props.tipTotal <= 0
      ? allocatedTotal === 0
      : Math.abs(remaining) < 0.001 &&
        rows.every(row => row.user_id && Number(row.amount) >= 0) &&
        rows.some(row => row.user_id && Number(row.amount) > 0);

  useEffect(() => {
    if (!isValid) {
      props.onChange(null);
      return;
    }
    props.onChange(
      rows
        .filter(row => row.user_id && Number(row.amount) > 0)
        .map(row => ({
          user_id: Number(row.user_id),
          amount: roundMoney(Number(row.amount)),
        })),
    );
  }, [rows, isValid, props.tipTotal]);

  function updateRow(index: number, patch: Partial<{ user_id: number | null; amount: string }>) {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows(prev => [...prev, { user_id: null, amount: remaining > 0 ? remaining.toFixed(2) : "0.00" }]);
  }

  function removeRow(index: number) {
    setRows(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  function splitEqually() {
    const selectedIndexes = rows.map((row, index) => (row.user_id ? index : -1)).filter(index => index >= 0);
    if (selectedIndexes.length === 0 || props.tipTotal <= 0) {
      return;
    }
    const share = roundMoney(props.tipTotal / selectedIndexes.length);
    let assigned = 0;
    setRows(prev =>
      prev.map((row, index) => {
        if (!row.user_id) {
          return row;
        }
        const isLast = index === selectedIndexes[selectedIndexes.length - 1];
        const amount = isLast ? roundMoney(props.tipTotal - assigned) : share;
        if (!isLast) {
          assigned = roundMoney(assigned + share);
        }
        return { ...row, amount: amount.toFixed(2) };
      }),
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        {t("Split this tip among servers. Amounts must add up to")}{" "}
        <LocaleCurrency currency={props.currency ?? "cad"} amount={props.tipTotal} />.
      </p>
      {rows.map((row, index) => (
        <div key={index} className="flex gap-2 items-end">
          <div style={{ flex: 1 }}>
            <Select
              label={t("Server")}
              onChange={(value: number) => updateRow(index, { user_id: value })}
              defaultValue={row.user_id ?? null}
            >
              {staffAccounts.map((user: { id: number; full_name?: string }) => (
                <Option key={user.id} value={user.id} name={user.full_name}>
                  {user.full_name}
                </Option>
              ))}
            </Select>
          </div>
          <div style={{ width: 120 }}>
            <Input
              label={t("Amount")}
              type="number"
              min={0}
              step="0.01"
              prefix="$"
              value={row.amount}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRow(index, { amount: e.target.value })}
              labelStyle={{ color: props.darkStyle ? "#FFFFFF" : undefined }}
            />
          </div>
          <ButtonNew type="text" size="small" onClick={() => removeRow(index)} disabled={rows.length <= 1}>
            {t("Remove")}
          </ButtonNew>
        </div>
      ))}
      <div className="flex gap-2 items-center flex-wrap">
        <ButtonNew type="secondary" size="small" onClick={addRow}>
          {t("Add server")}
        </ButtonNew>
        <ButtonNew type="secondary" size="small" onClick={splitEqually}>
          {t("Split equally")}
        </ButtonNew>
        <span className={`text-sm ml-auto ${Math.abs(remaining) < 0.001 ? "text-gray-600" : "text-red-600"}`}>
          {t("Remaining")}: <LocaleCurrency currency={props.currency ?? "cad"} amount={remaining} />
        </span>
      </div>
    </div>
  );
}
